import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { HOME } from "@/constants/testIds";

const CharReveal = ({ text, className = "" }) => (
  <span className={`inline-flex ${className}`}>
    {text.split("").map((ch, i) => (
      <span key={i} className="mask-line inline-block" style={{ overflow: "hidden" }}>
        <motion.span
          initial={{ y: "115%" }}
          whileInView={{ y: "0%" }}
          viewport={{ once: false, margin: "-20%" }}
          transition={{
            duration: 0.7,
            delay: 0.02 * i,
            ease: [0.76, 0, 0.24, 1],
          }}
          className="inline-block"
        >
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      </span>
    ))}
  </span>
);

const Manifesto = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bigX = useTransform(scrollYProgress, [0, 1], ["10vw", "-40vw"]);
  const bigOpacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [0, 0.06, 0.06, 0]
  );

  return (
    <section
      ref={ref}
      data-testid={HOME.manifesto}
      className="relative py-32 md:py-48 px-6 md:px-10 bg-[#050505] overflow-hidden"
    >
      {/* Background giant word (parallax) */}
      <motion.div
        style={{ x: bigX, opacity: bigOpacity }}
        className="pointer-events-none absolute top-1/2 -translate-y-1/2 font-editorial italic text-white text-[40vw] leading-none whitespace-nowrap select-none"
        aria-hidden="true"
      >
        Manifesto ·
      </motion.div>

      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center gap-3 text-[0.7rem] tracking-hair uppercase text-white/60 mb-16 md:mb-24">
          <motion.span
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="inline-block w-10 h-px bg-white/40 origin-left"
          />
          {t.manifesto.kicker}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          {t.manifesto.chapters.map((ch, i) => (
            <motion.div
              key={ch.n}
              initial={{ opacity: 0, y: 80 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: "-15%" }}
              transition={{ duration: 1.1, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`md:col-span-4 ${i === 1 ? "md:mt-24" : ""} ${i === 2 ? "md:mt-48" : ""}`}
            >
              <div className="flex items-baseline gap-4 mb-6">
                <span className="font-display text-white/40 text-3xl md:text-4xl">
                  {ch.n}
                </span>
                <motion.span
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: false }}
                  transition={{
                    duration: 0.9,
                    delay: 0.3 + i * 0.12,
                    ease: [0.76, 0, 0.24, 1],
                  }}
                  className="h-px flex-1 bg-white/25 origin-left"
                />
              </div>
              <h3 className="font-editorial italic text-white text-4xl md:text-5xl lg:text-6xl mb-6 leading-[0.98]">
                <CharReveal text={ch.title} />
              </h3>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: false, margin: "-15%" }}
                transition={{ duration: 1, delay: 0.5 + i * 0.12 }}
                className="text-white/70 text-base md:text-[1.05rem] leading-relaxed max-w-sm"
              >
                {ch.body}
              </motion.p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
